import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import Tooltip from '../../components/common/Tooltip';
import { selectUser } from '../../services/reducers/user';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import DataTable from 'react-data-table-component';
import AdminOrderDrawer from '../../components/admin/AdminOrderDrawer';
import { API_URL } from '../../config/api';

const AdminOrdersPage = () => {
  const user = useSelector(selectUser);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  
  // Filtros
  const [filters, setFilters] = useState({
    status: 'all',
    paymentMethod: 'all',
    dateRange: 'all',
    producer: 'all',
    product: 'all'
  });


  // Carregar pedidos (TODOS os pedidos - visão administrativa)
  useEffect(() => {
    loadOrders();
  }, [filters]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const queryParams = new URLSearchParams({
        role: 'admin', // Importante: marca que é visão administrativa
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.paymentMethod !== 'all' && { payment_method: filters.paymentMethod }),
        ...(filters.producer !== 'all' && { producer_id: filters.producer }),
        ...(filters.product !== 'all' && { product_id: filters.product }),
        ...(filters.dateRange !== 'all' && getDateRangeParams(filters.dateRange))
      });

      const response = await fetch(`${API_URL}/api/admin/orders?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar pedidos');
      }

      const data = await response.json();
      setOrders(data.data || []);
      
    } catch (err) {
      setError(err.message);
      console.error('Erro ao carregar pedidos:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDateRangeParams = (range) => {
    const now = new Date();
    const startDate = new Date();

    switch (range) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        return {
          start_date: startDate.toISOString().split('T')[0],
          end_date: now.toISOString().split('T')[0]
        };
      case 'last7days':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'last30days':
        startDate.setDate(now.getDate() - 30);
        break;
      case 'last3months':
        startDate.setMonth(now.getMonth() - 3);
        break;
      default:
        return {};
    }

    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: now.toISOString().split('T')[0]
    };
  };

  const openOrderDetails = async (orderId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/orders/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar detalhes do pedido');
      }

      const data = await response.json();
      setSelectedOrder(data.data);
      setSelectedOrderId(orderId);
      setShowDrawer(true);
    } catch (err) {
      setError(err.message);
    }
  };


  // ===== Funções de formatação e utilitários =====
  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending_payment': { color: 'warning', icon: 'solar:clock-circle-bold', text: 'Aguardando Pagamento' },
      'paid': { color: 'info', icon: 'solar:check-circle-bold', text: 'Pago' },
      'processing': { color: 'primary', icon: 'solar:settings-bold', text: 'Em Preparação' },
      'shipped': { color: 'success', icon: 'solar:delivery-bold', text: 'Em Rota' },
      'awaiting_pickup': { color: 'warning', icon: 'solar:box-bold', text: 'Aguardando Retirada' },
      'delivered': { color: 'success', icon: 'solar:home-2-bold', text: 'Entregue' },
      'cancelled': { color: 'danger', icon: 'solar:close-circle-bold', text: 'Cancelado' },
      'failed': { color: 'danger', icon: 'solar:close-circle-bold', text: 'Frustrado' }
    };

    const config = statusConfig[status] || statusConfig['pending_payment'];
    
    return (
      <span className={`badge bg-${config.color} text-white d-flex align-items-center gap-1`}>
        <Icon icon={config.icon} className="text-xs" />
        {config.text}
      </span>
    );
  };

  const getPaymentMethodIcon = (method) => {
    const methods = {
      'credit_card': 'mdi:credit-card',
      'debit_card': 'mdi:credit-card-outline',
      'pix': 'mdi:qrcode',
      'boleto': 'mdi:file-document',
      'after_pay': 'mdi:truck-delivery'
    };
    return methods[method] || 'mdi:credit-card';
  };

  const getPaymentMethodText = (method) => {
    const methods = {
      'credit_card': 'Cartão de Crédito',
      'debit_card': 'Cartão de Débito',
      'pix': 'PIX',
      'boleto': 'Boleto',
      'after_pay': 'Pagamento Após Entrega'
    };
    return methods[method] || method;
  };

  const getPaymentMethodColor = (method) => {
    const colors = {
      'credit_card': '#6366f1',
      'debit_card': '#8b5cf6',
      'pix': '#10b981',
      'boleto': '#f59e0b',
      'after_pay': '#dc2626'
    };
    return colors[method] || '#6b7280';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (priceInCents) => {
    return CurrencyFormatter.centsToFormatted(priceInCents);
  };

  const getSimpleStatusBadge = (row) => {
    const statusConfig = {
      'pending_payment': { color: 'warning', text: 'Pendente' },
      'paid': { color: 'success', text: 'Pago' },
      'processing': { color: 'info', text: 'Processando' },
      'shipped': { color: 'primary', text: 'Enviado' },
      'awaiting_pickup': { color: 'warning', text: 'Aguardando' },
      'delivered': { color: 'success', text: 'Entregue' },
      'cancelled': { color: 'danger', text: 'Cancelado' },
      'failed': { color: 'danger', text: 'Falhou' }
    };

    const config = statusConfig[row.sale_status] || statusConfig['pending_payment'];
    
    return (
      <span className={`badge bg-${config.color} text-white`} style={{ fontSize: '11px' }}>
        {config.text}
      </span>
    );
  };

  // ===== Legenda de status com ícones coloridos =====
  const StatusLegend = () => (
    <div className="d-flex flex-wrap align-items-center justify-content-end gap-4 mb-3">
      <div className="d-flex align-items-center gap-2">
        <div 
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: '#3b82f620',
            border: '2px solid #3b82f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon icon="solar:delivery-bold" style={{ fontSize: '12px', color: '#3b82f6' }} />
        </div>
        <span className="text-secondary" style={{ fontSize: '12px' }}>Em Rota | Em Preparação</span>
      </div>
      <div className="d-flex align-items-center gap-2">
        <div 
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: '#10b98120',
            border: '2px solid #10b981',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon icon="solar:check-circle-bold" style={{ fontSize: '12px', color: '#10b981' }} />
        </div>
        <span className="text-secondary" style={{ fontSize: '12px' }}>Pago | Entregue</span>
      </div>
      <div className="d-flex align-items-center gap-2">
        <div 
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: '#f59e0b20',
            border: '2px solid #f59e0b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon icon="solar:clock-circle-bold" style={{ fontSize: '12px', color: '#f59e0b' }} />
        </div>
        <span className="text-secondary" style={{ fontSize: '12px' }}>Aguardando Pagamento | Aguardando Retirada</span>
      </div>
      <div className="d-flex align-items-center gap-2">
        <div 
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: '#ef444420',
            border: '2px solid #ef4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon icon="solar:close-circle-bold" style={{ fontSize: '12px', color: '#ef4444' }} />
        </div>
        <span className="text-secondary" style={{ fontSize: '12px' }}>Cancelado | Frustrado</span>
      </div>
    </div>
  );

  // ===== Função para status apenas com pontos coloridos =====
  const getStatusDot = (status) => {
    let color = '#6B7280'; // default gray
    
    if (['paid', 'delivered'].includes(status)) color = '#10B981'; // verde
    else if (['shipped', 'processing'].includes(status)) color = '#3B82F6'; // azul
    else if (['pending_payment', 'awaiting_pickup'].includes(status)) color = '#F59E0B'; // amarelo
    else if (['cancelled', 'failed'].includes(status)) color = '#EF4444'; // vermelho
    
    return <span title={getStatusText(status)} style={{ width: 10, height: 10, borderRadius: 10, display: 'inline-block', backgroundColor: color }} />;
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending_payment': 'Aguardando Pagamento',
      'paid': 'Pago',
      'processing': 'Em Preparação',
      'shipped': 'Em Rota',
      'awaiting_pickup': 'Aguardando Retirada',
      'delivered': 'Entregue',
      'cancelled': 'Cancelado',
      'failed': 'Frustrado'
    };
    return statusMap[status] || status;
  };

  // ===== Busca global =====
  const [searchText, setSearchText] = useState('');
  const [isSmall, setIsSmall] = useState(false);

  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const filteredOrders = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    if (!text) return orders;
    return orders.filter((order) => {
      return (
        String(order.id).includes(text) ||
        order.customer_name?.toLowerCase().includes(text) ||
        order.product_name?.toLowerCase().includes(text) ||
        order.producer_name?.toLowerCase().includes(text)
      );
    });
  }, [orders, searchText]);


  // ===== Definição das colunas da tabela (SIMPLIFICADA) =====
  const baseColumns = [
    {
      name: 'ID',
      selector: row => row.id,
      sortable: true,
      width: '100px',
      minWidth: '100px',
      maxWidth: '120px',
      cell: row => (
        <div className="text-center">
          <span className="fw-medium text-dark">#{row.id}</span>
        </div>
      )
    },
    {
      name: 'Produto',
      selector: row => row.product_name || '',
      sortable: true,
      grow: 2,
      minWidth: '200px',
      cell: row => (
        <span className="text-sm fw-medium" style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'block' }}>
          {row.product_name || '-'}
        </span>
      )
    },
    {
      name: 'Comprador',
      selector: row => row.customer_name || '',
      sortable: true,
      grow: 2,
      minWidth: '200px',
      cell: row => (
        <span className="text-sm fw-medium" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.customer_name || '-'}
        </span>
      )
    },
    {
      name: 'Data',
      selector: row => row.created_at,
      sortable: true,
      width: '160px',
      minWidth: '160px',
      maxWidth: '180px',
      cell: row => <span style={{ fontSize: '12px' }}>{formatDate(row.created_at)}</span>
    },
    {
      name: 'Produtor',
      selector: row => row.producer_name || '',
      sortable: true,
      grow: 2,
      minWidth: '200px',
      cell: row => (
        <span className="text-sm fw-medium" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.producer_name || '-'}
        </span>
      )
    },
    {
      name: 'Status',
      selector: row => row.sale_status,
      sortable: true,
      width: '150px',
      minWidth: '150px',
      maxWidth: '180px',
      cell: row => (
        <div className="text-center">
          {getSimpleStatusBadge(row)}
        </div>
      )
    },
    {
      name: 'Ação',
      width: '100px',
      minWidth: '100px',
      maxWidth: '120px',
      cell: row => (
        <div className="text-center">
          <button
            title="Ver detalhes"
            onClick={() => openOrderDetails(row.id)}
            className="btn btn-outline-primary-600 text-primary-600 btn-sm d-flex align-items-center justify-content-center"
            style={{ width: 36, height: 32, padding: 0, margin: '0 auto' }}
          >
            <Icon icon="solar:eye-bold" />
          </button>
        </div>
      )
    }
  ];

  // Em telas pequenas, manter todas as colunas (já simplificadas)
  const visibleColumns = useMemo(() => {
    return baseColumns;
  }, [baseColumns]);

  // Linha expansível para mobile (removida - tabela já simplificada)

  return (
    <MasterLayout>
      <div className="dashboard-main-body" style={{ width: '100%', maxWidth: '100%', overflowX: 'visible' }}>
        <AdminPageHeader
          icon="solar:clipboard-list-bold"
          title="Pedidos"
          subtitle="Gerenciamento de todos os pedidos da plataforma"
        />

        {/* Filtros e Busca */}
        <div className="card radius-8 mb-3">
          <div className="card-body p-3">
            <div className="row g-3 align-items-end">
              <div className="col-md-2">
                <label className="form-label text-sm">Status</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="all">Todos</option>
                  <option value="pending_payment">Aguardando Pagamento</option>
                  <option value="paid">Pago</option>
                  <option value="processing">Em Preparação</option>
                  <option value="shipped">Em Rota</option>
                  <option value="awaiting_pickup">Aguardando Retirada</option>
                  <option value="delivered">Entregue</option>
                  <option value="cancelled">Cancelado</option>
                  <option value="failed">Frustrado</option>
                </select>
              </div>
              
              <div className="col-md-2">
                <label className="form-label text-sm">Pagamento</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.paymentMethod}
                  onChange={(e) => setFilters(prev => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  <option value="all">Todos</option>
                  <option value="credit_card">Cartão de Crédito</option>
                  <option value="pix">PIX</option>
                  <option value="boleto">Boleto</option>
                  <option value="after_pay">After Pay</option>
                </select>
              </div>
              
              <div className="col-md-2">
                <label className="form-label text-sm">Período</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.dateRange}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                >
                  <option value="all">Todos</option>
                  <option value="today">Hoje</option>
                  <option value="last7days">Últimos 7 dias</option>
                  <option value="last30days">Últimos 30 dias</option>
                  <option value="last3months">Últimos 3 meses</option>
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label text-sm">Buscar</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Buscar por ID, comprador, produto, produtor..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>

              <div className="col-md-2">
                <button
                  onClick={() => {
                    setFilters({
                      status: 'all',
                      paymentMethod: 'all',
                      dateRange: 'all',
                      producer: 'all',
                      product: 'all'
                    });
                    setSearchText('');
                  }}
                  className="btn btn-outline-secondary btn-sm w-100"
                >
                  <Icon icon="solar:refresh-bold" className="me-1" />
                  Limpar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Legenda de Status */}
        <StatusLegend />

        {/* Tabela de Pedidos */}
        <div className="card radius-8" style={{ width: '100%', maxWidth: '100%', margin: 0 }}>
          <div className="card-body p-0" style={{ width: '100%', overflow: 'visible', padding: 0 }}>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" />
                <p className="mt-2">Carregando pedidos...</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger m-3">
                <Icon icon="solar:danger-bold" className="me-2" />
                {error}
              </div>
            ) : (
              <>
              <div className="d-flex justify-content-between align-items-center mb-3 p-3 border-bottom">
                <div>
                  <h6 className="mb-0">Pedidos ({filteredOrders.length})</h6>
                  <small className="text-muted">Gerenciamento completo de todos os pedidos</small>
                </div>
              </div>

              <div style={{ width: '100%', overflowX: 'auto', display: 'block', margin: 0, padding: 0 }}>
                <style>{`
                  .rdt_Table {
                    width: 100% !important;
                    min-width: 100% !important;
                  }
                  .rdt_TableWrapper {
                    width: 100% !important;
                    min-width: 100% !important;
                  }
                  .rdt_TableRow {
                    width: 100% !important;
                  }
                `}</style>
                <div style={{ width: '100%', minWidth: '100%' }}>
                  <DataTable
                columns={visibleColumns}
                data={filteredOrders}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                highlightOnHover
                defaultSortFieldId={1}
                responsive={false}
                persistTableHead
                fixedHeader={false}
                fixedHeaderScrollHeight="600px"
                noDataComponent={
                  <div className="text-center py-4">
                    <Icon icon="solar:file-text-bold" style={{ fontSize: '48px', color: '#d1d5db' }} />
                    <p className="mt-2 text-muted">Nenhum pedido encontrado</p>
                  </div>
                }
                customStyles={{
                  headCells: {
                    style: {
                      backgroundColor: '#f8fafc',
                      fontWeight: '600',
                      fontSize: '13px',
                      color: '#374151',
                      whiteSpace: 'nowrap',
                      lineHeight: '1.4',
                      padding: '12px 8px',
                      textAlign: 'left',
                      borderBottom: '2px solid #e5e7eb'
                    }
                  },
                  rows: {
                    style: {
                      minHeight: '60px',
                      fontSize: '13px',
                      borderBottom: '1px solid #f3f4f6',
                      '&:hover': {
                        backgroundColor: '#f9fafb',
                        cursor: 'pointer'
                      }
                    }
                  },
                  cells: {
                    style: {
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      padding: '12px 8px',
                      verticalAlign: 'middle',
                      borderRight: 'none',
                      borderLeft: 'none'
                    }
                  },
                  table: {
                    style: {
                      width: '100%',
                      minWidth: '100%',
                      tableLayout: 'auto'
                    }
                  },
                  tableWrapper: {
                    style: {
                      width: '100%',
                      overflowX: 'auto',
                      display: 'block'
                    }
                  },
                  responsiveWrapper: {
                    style: {
                      width: '100%',
                      overflowX: 'auto'
                    }
                  },
                  headRow: {
                    style: {
                      width: '100%'
                    }
                  },
                  headCells: {
                    style: {
                      width: '100%'
                    }
                  }
                }}
              />
                </div>
              </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Drawer Detalhado de Pedido */}
      {showDrawer && selectedOrderId && (
        <AdminOrderDrawer
          show={showDrawer}
          onHide={() => {
            setShowDrawer(false);
            setSelectedOrder(null);
            setSelectedOrderId(null);
          }}
          orderId={selectedOrderId}
          order={selectedOrder}
          onOrderUpdated={() => {
            loadOrders();
          }}
        />
      )}
    </MasterLayout>
  );
};

export default AdminOrdersPage;
